import {
  type ApiResponse,
  HTTPMethod,
  request,
} from '../../global/api/legacyApi'

export type PollOptionDto = {
  /** ID of the poll option */
  poll_option_id: number
  /** The poll option */
  option: string | number
  /** Numbers of answers related to the poll option. Omitted for audience (token) requests. */
  answer_count?: number
  /** Percentage of answers related to the poll option, as a fraction (`'0.75'`) */
  answer_percentage?: number | string
  /** Formatted answers percentage (`'75 %'`) */
  answer_percentage_fmt?: string
}

export type PollDto = {
  /** ID of the Poll */
  poll_id: number
  /** ID of the object of which the poll belongs */
  object_id: number
  /** Type of the object of which the poll belongs */
  object_type: 'live' // only live supported atm.
  /** The poll question */
  question: string | number
  /* Date string of when the poll was opened. */
  opened_date: string
  /** Numbers of options related to the question of the poll */
  options_count: number
  /** Is the poll open/started */
  open_p: boolean
  /** Whether the poll results are displayed or not */
  display_results_p: boolean
  /** Total number of answers. Omitted for audience (token) requests. */
  answer_count?: number
  /** Options related to the question of the poll */
  options: Array<PollOptionDto>
}

type UpdatePollDto = {
  /** ID of the poll that was updated */
  poll_id: string
}

const pollEndpoint = () => ({
  list: () => {
    return `/api/2/poll/list`
  },
  update: () => '/api/2/poll/update',
  answer: () => '/api/2/poll/answer',
})

type GetPollsOptions = {
  /** Object token, required when the caller is not an authenticated producer */
  objectToken?: string
  /** Restrict the response to polls marked public */
  publicOnly?: boolean
}

/**
 * Get all polls for an object (webinar)
 *
 * @param {string} objId
 * @param {GetPollsOptions} options Optional token and public-only filter
 */
async function getPolls(
  objId: number,
  options: GetPollsOptions = {}
): Promise<PollDto[]> {
  const { data } = await request<ApiResponse<PollDto[]>>({
    method: HTTPMethod.GET,
    url: pollEndpoint().list(),
    data: {
      object_id: objId,
      object_token: options.objectToken,
      public_p: options.publicOnly ? 1 : undefined,
    },
  })
  return data
}

/**
 * Start a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function startPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<ApiResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: true,
    },
  })
  return data
}

/**
 * End a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function endPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<ApiResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: false,
    },
  })
  return data
}

async function togglePollResults(pollId: number, open: boolean) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      display_results_p: open,
    },
  })
}

type CreatePollResponse = {
  poll_id: number
}

async function createPoll(
  liveId: number,
  question: string
): Promise<CreatePollResponse> {
  const { data } = await request<ApiResponse<CreatePollResponse>>({
    method: HTTPMethod.POST,
    url: '/api/2/poll/add',
    data: {
      question,
      object_id: liveId,
    },
  })
  return data
}

async function updatePollTitle(pollId: number, title: string) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      question: title,
    },
  })
}

async function setPollOptions(pollId: number, options: string[]) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: '/api/2/poll/set-options',
    data: {
      options: JSON.stringify(options),
      poll_id: pollId,
    },
  })
}

/**
 * Register a vote on a poll option.
 *
 * @param {number} objectId ID of the object (webinar) the poll belongs to
 * @param {string} pollId ID of the poll being answered
 * @param {string} optionId ID of the chosen poll option
 */
async function answerPoll({
  objectId,
  objectToken,
  pollId,
  uuid,
  optionId,
}: {
  objectId: number
  objectToken?: string
  pollId: number
  uuid?: string
  optionId: number
}) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: pollEndpoint().answer(),
    data: {
      object_id: objectId,
      object_token: objectToken,
      poll_id: pollId,
      poll_option_id: optionId,
      uuid,
    },
  })
}

export {
  answerPoll,
  getPolls,
  startPoll,
  endPoll,
  createPoll,
  setPollOptions,
  togglePollResults,
  updatePollTitle,
}
